import { useState, useMemo } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  ShoppingBag,
  User,
  Menu,
  ChevronDown,
  Search as SearchIcon,
} from "lucide-react";
import { Container } from "@/components/atoms";
import { SearchModal } from "@/components/organisms/search-modal";
import { Logo } from "./logo";
import { useCartStore } from "@/stores/cart-store";
import { useUIStore } from "@/stores/ui-store";
import { usePopulatedCategories } from "@/hooks/use-categories";
import { useProducts } from "@/hooks/use-products";
import { cn } from "@/lib/cn";

const navLink = ({ isActive }: { isActive: boolean }) =>
  cn(
    "font-condensed text-sm font-bold uppercase tracking-wide transition-colors",
    isActive
      ? "text-healthy-blue"
      : "text-night-blue/80 hover:text-healthy-blue",
  );

export function Nav() {
  const itemCount = useCartStore((s) =>
    s.items.reduce((n, i) => n + i.quantity, 0),
  );
  const openMobileNav = useUIStore((s) => s.openMobileNav);
  const { data: categories = [] } = usePopulatedCategories();
  const { data: products = [] } = useProducts();
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const populatedSlugs = useMemo(
    () => new Set(products.map((p) => p.category)),
    [products],
  );

  const navCategories = useMemo(
    () => categories.filter((c) => c.active && populatedSlugs.has(c.slug)),
    [categories, populatedSlugs],
  );

  return (
    <header className="sticky top-0 z-40 border-b border-night-blue/10 bg-white">
      <Container className="flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openMobileNav}
            aria-label="Open menu"
            className="-ml-2 flex h-11 w-11 items-center justify-center text-night-blue lg:hidden"
          >
            <Menu size={24} />
          </button>
          <Logo />
        </div>

        <nav className="hidden items-center gap-7 lg:flex">
          <div
            className="relative"
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
          >
            <NavLink to="/shop" className={navLink}>
              <span className="inline-flex items-center gap-1">
                Shop <ChevronDown size={14} />
              </span>
            </NavLink>
            {megaOpen && (
              <div className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-3">
                <div className="grid grid-cols-2 gap-1 rounded-card border border-night-blue/10 bg-white p-4 shadow-xl">
                  {navCategories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/shop/${c.slug}`}
                      className="rounded-soft px-3 py-2 hover:bg-medical-grey"
                    >
                      <span className="block font-condensed font-bold text-night-blue">
                        {c.name}
                      </span>
                      <span className="block text-xs text-night-blue/70 line-clamp-1">
                        {c.description}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/about" className={navLink}>
            About
          </NavLink>
          <NavLink to="/cbd-101" className={navLink}>
            CBD 101
          </NavLink>
          <NavLink to="/faq" className={navLink}>
            FAQ
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          {/*
            DRB-67 — a single icon rather than an always-visible field. Baymard's autocomplete
            research recommends darkening the page while search is active so results are not
            competing with page content; a modal is the strongest form of that, and it gives the
            nav its space back. Costs one extra click on desktop, which is the real trade.
          */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search products"
            className="flex h-11 w-11 items-center justify-center text-night-blue/80 hover:text-leaf-green-ink"
          >
            <SearchIcon size={22} />
          </button>
          <Link
            to="/account"
            aria-label="Account"
            className="flex h-11 w-11 items-center justify-center text-night-blue/80 hover:text-leaf-green-ink"
          >
            <User size={22} />
          </Link>
          <Link
            to="/cart"
            aria-label="Cart"
            className="relative flex h-11 w-11 items-center justify-center text-night-blue/80 hover:text-leaf-green-ink"
          >
            <ShoppingBag size={22} />
            {itemCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-calm-orange-ink px-1 text-[10px] font-bold text-white">
                {itemCount}
              </span>
            )}
          </Link>
        </div>
      </Container>
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
